import type { ItineraryItem } from '@elara/client-core/models/ItineraryItemModel';

import type { ItineraryItemAction } from './BaseItineraryBlock.js';

interface BookingDialogs {
  /** Editable dialog for recording/updating a booking (confirmation, notes, attachments). */
  openBookingDialog: (item: ItineraryItem) => void;
}

/**
 * A "View booking details" primary action for items that have been booked, approved, or have
 * manually-recorded booking info. Mirrors the `ViewBookingDetailsRow` visibility rule.
 */
export function viewBookingDetailsAction(
  item: ItineraryItem,
  onView: (item: ItineraryItem) => void,
): ItineraryItemAction[] {
  if (!item.canViewBooking) return [];
  return [
    {
      type: 'primary',
      label: 'View booking',
      icon: 'confirmation_number',
      onClick: (e) => {
        e.stopPropagation();
        onView(item);
      },
    },
  ];
}

/**
 * The manual booking-entry action shared by the editable event cards (place, flight, hotel,
 * activity): a single button that opens the booking dialog to record an external booking, or
 * to edit the details (confirmation, notes, attachments) once something has been entered.
 *
 * `tones` sets the prominence of each affordance — `add` for the empty state, `edit` once details
 * exist (the details themselves render inline in the expanded card). Cancelled items get no action.
 */
export function bookingDetailActions(
  item: ItineraryItem,
  dialogs: BookingDialogs,
  tones: { add: ItineraryItemAction['type'] },
): ItineraryItemAction[] {
  if (!item.canAddBooking) return [];

  return [
    {
      type: tones.add,
      label: 'Add booking',
      icon: 'add',
      onClick: (e) => {
        e.stopPropagation();
        dialogs.openBookingDialog(item);
      },
    },
  ];
}
